import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Truck, X, RotateCcw, ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { loadLatestOrder, getCurrentStepIndex, STEP_ORDER, type TrackedOrder } from '../utils/orderTracking';
// Session-only dismiss key. We intentionally don't use localStorage —
// we want returning visitors to be greeted on every fresh visit.
const SESSION_DISMISS_KEY = 'welcome_back_dismissed_v1';
const VISITS_KEY = '253gravel_visits';
const LAST_ORDER_KEY = '253gravel_last_order';
const REORDER_CHIP_IMPRESSIONS_KEY = 'reorder_chip_impressions_v1';
const REORDER_CHIP_MAX_IMPRESSIONS = 3;
type SavedOrder = {
  items: {
    id?: string;
    category: string;
    product: string;
    quantity: number;
    pricePerYard: number;
  }[];
  addressInput?: string;
  name?: string;
  phone?: string;
  email?: string;
};
// Estimate the total cost for the saved order using the same small-load /
// bulk discount math the cart uses, so the headline number matches what
// the user will see in the cart after reorder.
function estimateOrderTotal(items: SavedOrder['items']): number {
  return items.reduce((sum, item) => {
    const n = (item.product || '').toLowerCase();
    const isLight =
    // Fill dirt removed — it's a heavy base material, not a
    // light landscape material like bark/compost/topsoil.
    n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('50/50') || n.includes('play chips');
    const minCharge = 3;
    const smallLoadThreshold = isLight ? 12 : 10;
    const billable = Math.max(item.quantity, minCharge);
    const surcharge = (() => {
      if (billable >= smallLoadThreshold) return 1;
      return 1 + (smallLoadThreshold - billable) * 0.1;
    })();
    const effective = Math.ceil(item.pricePerYard * surcharge);
    const fullLoad = isLight ? 15 : 12;
    const bulkPct = item.quantity >= fullLoad * 4 ? 0.1 : item.quantity >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
    let total = billable * effective;
    if (bulkPct > 0) total = Math.round(total * (1 - bulkPct));
    return sum + total;
  }, 0);
}
// Read last-order data from localStorage once and expose it along with a
// stable reorder action. Used by both the WelcomeBackCard and the inline
// ReorderChip so they stay in sync.
function useLastOrder() {
  const {
    loadSavedCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  const [savedOrder, setSavedOrder] = useState<SavedOrder | null>(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_ORDER_KEY);
      if (!raw) return;
      const parsed: SavedOrder = JSON.parse(raw);
      if (!parsed?.items?.length) return;
      setSavedOrder(parsed);
    } catch {}
  }, []);
  const handleReorder = () => {
    if (!savedOrder) return;
    loadSavedCart(savedOrder.items.map(i => ({
      id: `${i.category}-${i.product}-${Date.now()}-${Math.random()}`,
      category: i.category,
      product: i.product,
      quantity: i.quantity,
      pricePerYard: i.pricePerYard
    })));
    if (savedOrder.addressInput) {
      setDeliveryInfo(savedOrder.addressInput, 0, null);
    }
    openCart();
  };
  return {
    savedOrder,
    handleReorder
  };
}
// Slim inline pill version used inside the Hero estimator card. Catches
// returning visitors who dismissed the big welcome card but still want
// one-tap reorder from the place they actually start a new estimate.
// Auto-dismisses after REORDER_CHIP_MAX_IMPRESSIONS page loads without a
// reorder — counter is keyed to the saved order's contents so a new
// order resets the visibility window.
export function ReorderChip() {
  const {
    savedOrder,
    handleReorder
  } = useLastOrder();
  const [shouldShow, setShouldShow] = useState(false);
  useEffect(() => {
    if (!savedOrder) {
      setShouldShow(false);
      return;
    }
    try {
      // Stable signature of the saved order — if items change (new order
      // placed), we start the impression count from scratch.
      const sig = JSON.stringify(savedOrder.items.map(i => ({
        p: i.product,
        q: i.quantity,
        c: i.category
      })));
      const raw = localStorage.getItem(REORDER_CHIP_IMPRESSIONS_KEY);
      let data: {
        sig: string;
        count: number;
      } | null = raw ? JSON.parse(raw) : null;
      if (!data || data.sig !== sig) {
        data = {
          sig,
          count: 1
        };
      } else {
        data.count += 1;
      }
      localStorage.setItem(REORDER_CHIP_IMPRESSIONS_KEY, JSON.stringify(data));
      setShouldShow(data.count <= REORDER_CHIP_MAX_IMPRESSIONS);
    } catch {
      setShouldShow(true);
    }
  }, [savedOrder]);
  if (!savedOrder || !shouldShow) return null;
  const firstItem = savedOrder.items[0];
  const summary = savedOrder.items.length > 1 ? `${savedOrder.items.reduce((s, i) => s + i.quantity, 0)} yds × ${savedOrder.items.length}` : `${firstItem.quantity} yds ${firstItem.product}`;
  return <button type="button" onClick={handleReorder} className="group w-full inline-flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 hover:border-[#059F40]/50 transition-all text-left" data-id="element-1969">
      <span className="flex items-center gap-2.5 min-w-0" data-id="element-1970">
        <RotateCcw className="w-3.5 h-3.5 text-[#059F40] shrink-0" data-id="element-1971" />
        <span className="text-xs sm:text-sm text-white/80 truncate" data-id="element-1972">
          <span className="text-white/60" data-id="element-1973">Reorder last:</span>{' '}
          <span className="font-semibold text-white" data-id="element-1974">{summary}</span>
        </span>
      </span>
      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#059F40] shrink-0" data-id="element-1975">
        Reorder
        <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-1976" />
      </span>
    </button>;
}
export function WelcomeBackCard() {
  const {
    items,
    loadSavedCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  const [savedOrder, setSavedOrder] = useState<SavedOrder | null>(null);
  const [visits, setVisits] = useState(1);
  const [dismissed, setDismissed] = useState(false);
  // Active-order detection — if the customer has a TrackedOrder saved from
  // this device that hasn't been delivered yet, we swap the "reorder" CTA
  // for a one-tap "Track delivery" CTA. This is the same data the email
  // confirmation links to, but surfaced contextually in the Hero where the
  // customer naturally lands when they return to the site mid-order.
  const [activeOrder, setActiveOrder] = useState<TrackedOrder | null>(null);
  const [activeStepIdx, setActiveStepIdx] = useState<number>(0);
  useEffect(() => {
    try {
      // If user already dismissed this session, stay hidden.
      if (sessionStorage.getItem(SESSION_DISMISS_KEY)) {
        setDismissed(true);
        return;
      }
      const raw = localStorage.getItem(LAST_ORDER_KEY);
      if (!raw) return;
      const parsed: SavedOrder = JSON.parse(raw);
      if (!parsed?.items?.length) return;
      setSavedOrder(parsed);
      // Increment visit counter (used to tune the greeting tone).
      const v = parseInt(localStorage.getItem(VISITS_KEY) || '1', 10);
      setVisits(v);
      localStorage.setItem(VISITS_KEY, String(v + 1));
      // Check for an in-progress order from THIS device. The order is
      // considered "active" until the delivery window closes (step 4).
      const latest = loadLatestOrder();
      if (latest) {
        const stepIdx = getCurrentStepIndex(latest);
        if (stepIdx < STEP_ORDER.length - 1) {
          setActiveOrder(latest);
          setActiveStepIdx(stepIdx);
        }
      }
    } catch {}
  }, []);
  // Don't show if cart already has items — they're mid-order.
  if (dismissed || !savedOrder || items.length > 0) return null;
  const firstName = (savedOrder.name || '').trim().split(/\s+/)[0] || 'there';
  const totalYards = savedOrder.items.reduce((s, i) => s + i.quantity, 0);
  const itemSummary = savedOrder.items.length > 1 ? `${totalYards} yds across ${savedOrder.items.length} materials` : `${savedOrder.items[0].quantity} yds ${savedOrder.items[0].product}`;
  const shortAddress = (savedOrder.addressInput || '').split(',')[0].trim();
  const estTotal = estimateOrderTotal(savedOrder.items);
  // Greeting copy adapts to whether they're tracking an active order or
  // returning between orders. Active-order takes priority — it's the more
  // urgent, helpful surface in that moment.
  const greeting = activeOrder ? `Hi ${firstName} — your load is on the way` : visits >= 2 ? `Need another load, ${firstName}?` : `Welcome back, ${firstName} 👋`;
  // Human label for the active-order step, mapped to the same progression
  // the /track page uses. Keeps the messaging consistent across surfaces.
  const stepLabel = (() => {
    switch (STEP_ORDER[activeStepIdx]) {
      case 'confirmed':
        return 'Order confirmed';
      case 'scheduled':
        return 'Scheduled for delivery';
      case 'dispatched':
        return 'Dispatched from yard';
      case 'en_route':
        return 'Driver en route';
      default:
        return 'In progress';
    }
  })();
  const handleReorder = () => {
    // Load items first so the cart has something to checkout.
    loadSavedCart(savedOrder.items.map(i => ({
      id: `${i.category}-${i.product}-${Date.now()}-${Math.random()}`,
      category: i.category,
      product: i.product,
      quantity: i.quantity,
      pricePerYard: i.pricePerYard
    })));
    // Push the saved delivery address into context. CartDrawer's
    // geocode-on-change effect will recompute fee + zone automatically.
    if (savedOrder.addressInput) {
      setDeliveryInfo(savedOrder.addressInput, 0, null);
    }
    openCart();
  };
  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {}
    setDismissed(true);
  };
  return <AnimatePresence data-id="element-1977">
      <motion.div initial={{
      opacity: 0,
      y: -8,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -8,
      scale: 0.98
    }} transition={{
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }} className="relative mb-6 md:mb-8 max-w-3xl mx-auto md:mx-0 rounded-xl bg-black/35 backdrop-blur-md border border-white/15 px-4 py-3 sm:px-5 sm:py-3.5 shadow-[0_8px_30px_rgba(0,0,0,0.35)]" role="region" aria-label="Welcome back" data-id="element-1978">
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-1979">
          <div className="hidden sm:flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-[#059F40]/15 border border-[#059F40]/30" data-id="element-1980">
            <Truck className="w-5 h-5 text-[#059F40]" data-id="element-1981" />
          </div>

          <div className="flex-1 min-w-0" data-id="element-1982">
            <p className="text-sm sm:text-base font-bold text-white leading-tight" data-id="element-1983">
              {greeting}
            </p>
            {activeOrder ? <p className="text-xs sm:text-sm text-white/70 mt-0.5 truncate" data-id="element-1984">
                <span className="inline-flex items-center gap-1 text-[#6FE39A] font-semibold" data-id="element-1985">
                  <span className="relative flex h-2 w-2" data-id="element-1986">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059F40] opacity-75" data-id="element-1987" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#059F40]" data-id="element-1988" />
                  </span>
                  {stepLabel}
                </span>
                <span className="text-white/40 mx-1.5" data-id="element-1989">·</span>
                <span className="text-white/90 font-medium" data-id="element-1990">{itemSummary}</span>
                {shortAddress && <>
                    {' '}
                    → <span className="text-white/90" data-id="element-1991">{shortAddress}</span>
                  </>}
              </p> : <p className="text-xs sm:text-sm text-white/70 mt-0.5 truncate" data-id="element-1992">
                Last load:{' '}
                <span className="text-white/90 font-medium" data-id="element-1993">{itemSummary}</span>
                {shortAddress && <>
                    {' '}
                    → <span className="text-white/90" data-id="element-1994">{shortAddress}</span>
                  </>}
              </p>}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0" data-id="element-1995">
            {activeOrder ?
          // One-tap track CTA. Uses the same /track/{orderId} route the
          // confirmation email links to — no order number or email
          // lookup required because the customer is on the device that
          // placed the order. This replaces the "Track your delivery"
          // override that used to live in the announcement bar.
          <Link to={`/track/${encodeURIComponent(activeOrder.orderId)}`} className="group inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg bg-[#059F40] hover:bg-[#048F3A] text-white font-bold text-xs sm:text-sm transition-colors shadow-[0_0_14px_rgba(5,159,64,0.3)]" aria-label="Track your delivery" data-id="element-1996">
                <MapPin className="w-3.5 h-3.5" data-id="element-1997" />
                <span className="hidden sm:inline" data-id="element-1998">Track delivery</span>
                <span className="sm:hidden" data-id="element-1999">Track</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-2000" />
              </Link> : <button type="button" onClick={handleReorder} className="group inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg bg-[#059F40] hover:bg-[#048F3A] text-white font-bold text-xs sm:text-sm transition-colors shadow-[0_0_14px_rgba(5,159,64,0.3)]" data-id="element-2001">
                <RotateCcw className="w-3.5 h-3.5" data-id="element-2002" />
                <span className="hidden sm:inline" data-id="element-2003">Reorder · ${estTotal}</span>
                <span className="sm:hidden" data-id="element-2004">${estTotal}</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-2005" />
              </button>}
            <button type="button" onClick={handleDismiss} aria-label="Dismiss" className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-id="element-2006">
              <X className="w-4 h-4" data-id="element-2007" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}